'use client';

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import useEmblaCarousel from 'embla-carousel-react';
import { formatarData } from '@/lib/utils';

// ============================================
// Types
// ============================================

type NewsItem = {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  slug?: string;
};

type NewsSectionProps = {
  title?: string;
  news: NewsItem[]; // Dados vindo da API
  className?: string;
  viewAllHref?: string;
};

// ============================================
// Utils
// ============================================

const stripInlineStyles = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/\s*style="[^"]*"/gi, '')
    .replace(/\s*style='[^']*'/gi, '');
};

const formatNewsDate = (dateStr: string): string => {
  return formatarData(dateStr);
};

// ============================================
// Components
// ============================================

const DateFlagLarge: React.FC<{ date: string }> = ({ date }) => {
 // const parts = date.split(' ');
  //const month = parts[0];
 // const dayAndYear = parts.slice(1).join(' ');

  return (
    <div className="relative w-20 h-16 sm:w-24 sm:h-20">
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 93 73"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0"
        preserveAspectRatio="none"
      >
        <path
          d="M0 43.9732V1.67816C0 0.751338 0.751337 0 1.67816 0H66.8116C67.2961 0 67.757 0.209437 68.0757 0.574434L91.705 27.6376C91.972 27.9433 92.1191 28.3355 92.1191 28.7413V71.3218C92.1191 72.2487 91.3677 73 90.4409 73H24.7193C24.2245 73 23.7548 72.7816 23.4359 72.4031L0.394766 45.0545C0.139819 44.7519 0 44.3689 0 43.9732Z"
          fill="#F7941D"
        />
      </svg>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute  left-1/3 translate-x-1/2 top-2">
        <path d="M4.89942 0.546875V1.63559H8.16555V0.546875H9.25426V1.63559H11.4317C11.7323 1.63559 11.976 1.87931 11.976 2.17994V10.8896C11.976 11.1903 11.7323 11.434 11.4317 11.434H1.63328C1.33265 11.434 1.08893 11.1903 1.08893 10.8896V2.17994C1.08893 1.87931 1.33265 1.63559 1.63328 1.63559H3.81071V0.546875H4.89942ZM10.8873 5.99043H2.17764V10.3453H10.8873V5.99043ZM4.35506 7.07914V8.16785H3.26635V7.07914H4.35506ZM7.07684 7.07914V8.16785H5.98813V7.07914H7.07684ZM9.79862 7.07914V8.16785H8.70991V7.07914H9.79862ZM3.81071 2.7243H2.17764V4.90172H10.8873V2.7243H9.25426V3.81301H8.16555V2.7243H4.89942V3.81301H3.81071V2.7243Z" fill="var(--grey-ligth-1, #F9F7F7)" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[#F9F7F7] text-[9px] sm:text-[11px] leading-tight font-semibold uppercase text-center px-1 max-w-[80px]">
        {formatNewsDate(date)}
      </span>
    </div>
  );
};

/**
 * DateBadge otimizado - Tailwind + SVG minimalista
 */
const DateBadgeSmall: React.FC<{ date: string }> = ({ date }) => (
  <div className="inline-flex items-center gap-2.5 px-3 py-1.5 backdrop-blur-sm relative">
    {/* Ícone calendário */}
    <div data-svg-wrapper className="absolute inset-0">
      <svg width="296" height="100%" viewBox="0 0 256 32" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path d="M0 16.3823V0H137.368L155.05 15.0411V31.6271H17.245L0 16.3823Z" fill="#C3C1C0" fillOpacity="0.4" />
      </svg>
    </div>
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      className="flex-shrink-0"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.92 0.55V1.64H8.2V0.55H9.29V1.64H11.48C11.78 1.64 12.03 1.88 12.03 2.19V10.93C12.03 11.23 11.78 11.48 11.48 11.48H1.64C1.34 11.48 1.09 11.23 1.09 10.93V2.19C1.09 1.88 1.34 1.64 1.64 1.64H3.83V0.55H4.92ZM10.93 6.01H2.19V10.39H10.93V6.01ZM4.37 7.11V8.2H3.28V7.11H4.37ZM7.11 7.11V8.2H6.01V7.11H7.11ZM9.84 7.11V8.2H8.75V7.11H9.84ZM3.83 2.73H2.19V4.92H10.93V2.73H9.29V3.83H8.2V2.73H4.92V3.83H3.83V2.73Z"
        fill="#616161"
      />
    </svg>

    {/* Texto data */}
    <span className="text-xs font-semibold font-poppins uppercase text-[#616161] tracking-wide">
      {formatNewsDate(date)}
    </span>
  </div>
);

// Featured Card
interface FeaturedCardProps {
  item: NewsItem;
}

const FeaturedCard: React.FC<FeaturedCardProps> = ({ item }) => {
  const clean = useMemo(() => stripInlineStyles(item.excerpt), [item.excerpt]);

  return (
    <Link href={`/noticias-eventos/noticias/${item.slug}`} className="block h-full">
      <article className="group relative w-full h-60 sm:h-80 md:h-96 lg:h-[580px] rounded-sm overflow-hidden shadow-md lg:shadow-[0px_8px_20px_rgba(139,141,142,0.15)] transition-all duration-300 hover:shadow-lg cursor-pointer">
        <Image
          src={item.image || '/event.png'}
          alt={item.title}
          fill
          className="object-cover transition-transform duration-300 group-hover:scale-105"
          priority
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 712px"
        />

        {/* overlay escuro */}
        <div className="absolute inset-0 bg-[#2A2A2A]/50" />

        {/* bandeira da data */}
        <div className="absolute left-3 sm:left-8 top-3 sm:top-8 z-10">
          <DateFlagLarge date={item.date} />
        </div>

        {/* conteúdo */}
        <div className="absolute left-0 right-0 bottom-0 p-4 sm:p-8 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
          <h3 className="text-white text-base sm:text-xl md:text-2xl font-semibold leading-5 sm:leading-7 md:leading-8 font-nunito line-clamp-3">
            {item.title}
          </h3>
          <p
            className="mt-2 sm:mt-3 text-white/90 text-xs sm:text-sm md:text-base leading-4 sm:leading-5 md:leading-6 font-nunito line-clamp-2 sm:line-clamp-3"
            dangerouslySetInnerHTML={{ __html: clean }}
          />
          <div className="mt-3 sm:mt-4 text-amber-600 text-xs sm:text-sm md:text-base font-normal font-poppins hover:underline transition-all duration-200">
            + detalhes
          </div>
        </div>
      </article>
    </Link>
  );
};

// Mobile Carousel Card - Horizontal Layout
interface MobileCarouselCardProps {
  item: NewsItem;
}

const MobileCarouselCard: React.FC<MobileCarouselCardProps> = ({ item }) => {
  const clean = useMemo(() => stripInlineStyles(item.excerpt), [item.excerpt]);

  return (
    <Link href={`/noticias-eventos/noticias/${item.slug}`} className="block h-full">
      <article className="group flex gap-3 bg-white rounded-sm overflow-hidden shadow-md transition-all duration-300 h-full cursor-pointer">
        {/* Image - Left Side */}
        <div className="relative w-[120px] h-full shrink-0 bg-gradient-to-br from-gray-200 to-gray-300">
          <Image
            src={item.image || '/event.png'}
            alt={item.title}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="120px"
          />
        </div>

        {/* Content - Right Side */}
        <div className="flex-1 flex flex-col justify-start gap-2 py-3 pr-3 min-w-0">
          {/* Date Badge */}
          <div className="flex items-center gap-1.5">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              className="flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.92 0.55V1.64H8.2V0.55H9.29V1.64H11.48C11.78 1.64 12.03 1.88 12.03 2.19V10.93C12.03 11.23 11.78 11.48 11.48 11.48H1.64C1.34 11.48 1.09 11.23 1.09 10.93V2.19C1.09 1.88 1.34 1.64 1.64 1.64H3.83V0.55H4.92ZM10.93 6.01H2.19V10.39H10.93V6.01ZM4.37 7.11V8.2H3.28V7.11H4.37ZM7.11 7.11V8.2H6.01V7.11H7.11ZM9.84 7.11V8.2H8.75V7.11H9.84ZM3.83 2.73H2.19V4.92H10.93V2.73H9.29V3.83H8.2V2.73H4.92V3.83H3.83V2.73Z"
                fill="#616161"
              />
            </svg>
            <span className="text-[10px] font-medium font-poppins uppercase text-[#616161] tracking-wide">
              {formatNewsDate(item.date)}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-[#1a1a1a] text-sm font-semibold leading-tight font-nunito line-clamp-2">
            {item.title}
          </h3>

          {/* Excerpt */}
          <p
            className="text-[#616161] text-xs leading-tight font-nunito line-clamp-2"
            dangerouslySetInnerHTML={{ __html: clean }}
          />

          {/* Details Link */}
          <div className="text-[#F15A29] text-xs font-normal font-poppins hover:underline transition-all duration-200 text-left mt-auto">
            + detalhes
          </div>
        </div>
      </article>
    </Link>
  );
};

// List Item
interface ListItemProps {
  item: NewsItem;
}

export const ListItem: React.FC<ListItemProps> = ({ item }) => {
  const clean = useMemo(() => stripInlineStyles(item.excerpt), [item.excerpt]);

  return (
    <>
      {/* Item Container */}
      <Link href={`/noticias-eventos/noticias/${item.slug}`} className="block">
        <div className="group w-full py-5 sm:py-2 lg:pt-0 lg:pb-4 transition-all duration-300 cursor-pointer">
          {/* Main Row */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 lg:gap-6 items-start">
            {/* Image Container */}
            <div className="relative w-full sm:w-48 md:w-52 lg:w-56 h-36 sm:h-40 md:h-44 lg:h-40 rounded-sm overflow-hidden shrink-0 bg-linear-to-br from-gray-200 to-gray-300 shadow-sm group-hover:shadow-md transition-all duration-300">
              <Image
                src={item.image || '/event.png'}
                alt={item.title}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-110"
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 192px, (max-width: 1024px) 208px, 224px"
                quality={75}
              />

              {/* Overlay escuro */}
              <div className="absolute inset-0 bg-linear-to-t from-black/40 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
            </div>

            {/* Content Container */}
            <div className="relative flex-1 flex flex-col justify-start gap-2 min-w-0">
              {/* Date Badge - com animação */}
              <div className="flex items-center">
                <DateBadgeSmall date={item.date} />
              </div>

              {/* Title */}
              <h4 className="text-zinc-800 text-base sm:text-lg md:text-xl font-semibold leading-6 sm:leading-7 md:leading-8 font-nunito line-clamp-2 group-hover:text-amber-700 transition-colors duration-300">
                {item.title}
              </h4>

              {/* Description */}
              <div
                className="text-zinc-600 text-sm sm:text-base leading-5 sm:leading-6 font-nunito line-clamp-2 group-hover:text-zinc-700 transition-colors duration-300"
                dangerouslySetInnerHTML={{ __html: clean }}
              />

              {/* Link with Arrow Animation */}
              <div className="mt-1 inline-flex items-center gap-2 text-amber-600 text-sm sm:text-base font-normal font-poppins hover:text-amber-700 transition-all duration-300 group/btn">
                <span className="relative">
                  + detalhes
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-amber-600 transition-all duration-300 group-hover/btn:w-full" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>

      {/* Separator Line */}
      <div className="h-px w-full bg-linear-to-r mb-4 from-[#C3C1C0] via-[#C3C1C0]/50 to-transparent" />
    </>
  );
};

// ============================================
// Main Component
// ============================================

export const NewsSection: React.FC<NewsSectionProps> = ({
  title = 'Notícias',
  news,
  className = '',
  viewAllHref = '/noticias-eventos',
}) => {
  const router = useRouter();

  // Embla Carousel para mobile
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    skipSnaps: false,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  // Empty state
  if (!news || news.length === 0) {
    return (
      <section className={`w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16 px-4 sm:px-6 lg:px-8 ${className}`}>
        <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
          <p className="text-center text-gray-500 text-base sm:text-lg">Nenhuma notícia disponível</p>
        </div>
      </section>
    );
  }

  const [featured, ...rest] = news;
  const restItems = rest.slice(0, 3);

  return (
    <section className={`w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16 px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20 md:mt-24 ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
        {/* HEADER - Título + Botão */}
        <div className="mb-6 sm:mb-8 lg:mb-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-7 sm:leading-8 lg:leading-10 font-medium uppercase font-barlow text-[#1a1a1a] tracking-wide">
            {title}
          </h2>
          <button
            type="button"
            onClick={() => router.push(viewAllHref)}
            className="cursor-pointer rounded-md border-2 border-[#F15A29] px-4 sm:px-6 py-2 sm:py-3 flex items-center gap-2 hover:bg-[#F15A29] hover:text-white transition-colors group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F15A29] w-full sm:w-auto justify-center"
            aria-label="Ver todas as notícias"
          >
            <span className="text-[#F15A29] group-hover:text-white text-lg sm:text-xl md:text-2xl font-medium font-poppins leading-none">
              +
            </span>
            <span className="text-[#F15A29] group-hover:text-white text-sm sm:text-base font-medium font-poppins">
              Notícias
            </span>
          </button>
        </div>

        {/* LAYOUT PRINCIPAL */}
        {/* Desktop: 2 colunas */}
        <div className="hidden lg:grid grid-cols-[1fr_1fr] gap-8">
          {/* Coluna Esquerda - Destaque */}
          <FeaturedCard item={featured} />

          {/* Coluna Direita - Lista */}
          <div className="flex flex-col">
            {restItems.map((item) => (
              <ListItem
                key={item.id}
                item={item}
              />
            ))}
          </div>
        </div>

        {/* Tablet: Stacked */}
        <div className="hidden md:block lg:hidden space-y-8">
          <FeaturedCard item={featured} />
          <div className="flex flex-col">
            {restItems.map((item) => (
              <ListItem
                key={item.id}
                item={item}
              />
            ))}
          </div>
        </div>

        {/* Mobile: Destaque + Carrossel horizontal */}
        <div className="block md:hidden space-y-6">
          {/* Card Destaque - Sozinho */}
          <FeaturedCard item={featured} />

          {/* Carrossel horizontal com cards lado a lado (imagem + texto) */}
          <div className="overflow-hidden -mx-4 px-4" ref={emblaRef}>
            <div className="flex gap-4">
              {restItems.map((item) => (
                <div key={item.id} className="flex-[0_0_85%] min-w-0">
                  <div className="h-[160px]">
                    <MobileCarouselCard item={item} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Indicadores de navegação */}
          {restItems.length > 1 && (
            <div className="flex justify-center gap-2">
              {scrollSnaps.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => emblaApi?.scrollTo(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${index === selectedIndex ? 'w-8 bg-[#F15A29]' : 'w-6 bg-gray-300'
                    }`}
                  aria-label={`Ir para slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};